import axios from 'axios';
import { QueryClient } from 'react-query';
import type { AxiosResponse } from 'axios';

import { getConnectionStatus as getSlackConnectionStatus } from './slack/api';
import type { OtterIntegrationName } from '../types';

export const queryClient = new QueryClient();

/* eslint-disable camelcase */

export interface GoogleOptimizeSettings {
  tag_recordings: boolean;
}

export interface OptimizelySettings {
  tag_recordings: boolean;
}

export interface UnbounceSettings {
  tag_recordings: boolean;
}

export interface ZapierSettings {
  enabled: boolean;
}

export interface GoogleAnalyticsSettings {
  hjuid_dimension_index: string | null;
  tag_sessions: boolean;
  send_hotjar_id: boolean;
}

export interface MixpanelSettings {
  send_events: boolean;
}

export enum IntegrationTokenName {
  Slack = 'Slack',
  Optimizely = 'Optimizely',
  GoogleOptimize = 'GoogleOptimize',
  GoogleAnalytics = 'GoogleAnalytics',
  Zapier = 'Zapier',
  Hubspot = 'Hubspot',
  Mixpanel = 'Mixpanel',
  Unbounce = 'Unbounce',
}

export interface IntegrationToken {
  create_epoch_time: Date;
  id: string;
  name: IntegrationTokenName;
  settings:
    | GoogleAnalyticsSettings
    | OptimizelySettings
    | GoogleOptimizeSettings
    | ZapierSettings
    | MixpanelSettings
    | UnbounceSettings;
  user_id: number;
  user_name: string;
}

export interface OauthToken {
  created_epoch_time: Date;
  id: number;
  provider: string;
  user_id: number;
  user_name: string;
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

export interface OtterIntegrationStatus {
  enabled: boolean;
}

export interface SlackIntegrationStatus {
  enabled: boolean;
}

export interface AllIntegrationsStatus {
  tokens: IntegrationToken[];
  oAuthTokens: OauthToken[];
  slack: SlackIntegrationStatus;
  teams: OtterIntegrationStatus;
}

/* eslint-enable camelcase */

export const INTEGRATION_TOKENS_QUERY_KEY = 'integration_tokens';

/**
 * Get an array of tokens for a given site.
 * @param {string} siteId The site id for which we want integration tokens.
 */
export async function getTokens(siteId: string): Promise<IntegrationToken[]> {
  const response = await axios.get(`/api/v1/sites/${siteId}/tokens`);

  return response.data;
}

export const INTEGRATION_OAUTH_TOKENS_QUERY_KEY = 'integration_oauth_tokens';

/**
 * Get an array of oauth tokens for a given site.
 * @param {string} siteId The site id for which we want integration oauth tokens.
 */
export async function getOAuthTokens(siteId: string): Promise<OauthToken[]> {
  const response = await axios.get(`/api/v1/sites/${siteId}/oauth-tokens`);

  return response.data;
}

// Otter integrations
export const OTTER_QUERY_KEY = 'integration_otter';

export async function getOtterIntegrationStatus(
  siteId: string,
  integrationName: OtterIntegrationName,
): Promise<OtterIntegrationStatus> {
  const response = await axios.get(`/api/integrations/sites/${siteId}/status/${integrationName}`);

  return response.data;
}

export async function enableOtterIntegration(
  siteId: string,
  integrationName: OtterIntegrationName,
): Promise<AxiosResponse> {
  return axios.put(`/api/integrations/sites/${siteId}/status/${integrationName}/enable`);
}

export async function disableOtterIntegration(
  siteId: string,
  integrationName: OtterIntegrationName,
): Promise<AxiosResponse> {
  return axios.put(`/api/integrations/sites/${siteId}/status/${integrationName}/disable`);
}

export async function getSlackStatus(siteId: number): Promise<SlackIntegrationStatus> {
  if (!siteId) {
    return { enabled: false };
  }

  try {
    const res = await getSlackConnectionStatus(siteId);
    return { enabled: res.status === 200 };
  } catch (error) {
    // The API will return 404 if the token is not found so let's handle
    // this case gracefully
    return { enabled: false };
  }
}

export async function getAllIntegrationsStatus(
  siteId: string | null,
): Promise<AllIntegrationsStatus | null> {
  if (!siteId) {
    return null;
  }

  const responses = await Promise.all([
    getTokens(siteId),
    getOAuthTokens(siteId),
    getSlackStatus(Number(siteId)),
    getOtterIntegrationStatus(siteId, 'teams_webhook'),
  ]);

  return {
    tokens: responses[0],
    oAuthTokens: responses[1],
    slack: responses[2],
    teams: responses[3],
  };
}
